import { useState, useEffect } from 'react';
import { CustomerFormData } from '@/customers/application/types/CustomerTypes';
import { FormSectionTitle } from '@/common/presentation/components/forms/FormSectionTitle';
import FormikAutocomplete, { OptionType } from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { FormikTextField } from '@/common/presentation/components/forms/fields/FormikTextField';
import { Grid2, Collapse, Button, Box } from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import tid from '@/common/infrastructure/config/tid';
import { useListAccountTypesAutocomplete } from '@/customers/application/adapters/listAccountTypesAutocomplete';
import { useListCustomerTypesAutocomplete } from '@/customers/application/adapters/listCustomerTypesAutocomplete';
import { useCustomerFormOptionsTransformed } from '@/customers/presentation/hooks/useCustomerFormOptionsTransformed';
import LocationForm from '@/map/presentation/LocationForm';
import { TEXT_COLORS } from '@/common/presentation/constants/colors';
import { useFormikContext } from 'formik';

interface Option {
  value: string | number;
  label: string;
  key?: string;
}

interface CustomerModalFormFieldsProps {
  readonly isNew: boolean;
  readonly open?: boolean;
}

export default function CustomerModalFormFields({ isNew, open = true }: CustomerModalFormFieldsProps) {
  const [customerTypeSearchTerm, setCustomerTypeSearchTerm] = useState('');
  const [accountTypeSearchTerm, setAccountTypeSearchTerm] = useState('');
  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: 1.1,
    color: TEXT_COLORS.TEXT_PRIMARY
  };

  // Always expand Payment & Credit Terms when editing (isNew === false)
  // For new customers, start collapsed
  const [isPaymentTermsExpanded, setIsPaymentTermsExpanded] = useState(!isNew);

  // Get transformed form options - always fetch for modal form
  const {
    pricingSchemeOptions,
    termOptions,
    salesRegionOptions,
    salesAgentOptions,
    defaultSalesAgentOption,
    loading: formOptionsLoading,
    filterOptions
  } = useCustomerFormOptionsTransformed(false);

  // Get Formik context to set default sales agent value
  const { values, setFieldValue } = useFormikContext<CustomerFormData>();

  // Set default sales agent when creating new customer and default option is available
  useEffect(() => {
    if (
      isNew &&
      defaultSalesAgentOption &&
      typeof defaultSalesAgentOption.value === 'number' &&
      !values.salesAgentId &&
      !formOptionsLoading
    ) {
      setFieldValue('salesAgentId', defaultSalesAgentOption.value);
    }
  }, [isNew, defaultSalesAgentOption, values.salesAgentId, formOptionsLoading, setFieldValue]);

  // Customer Type autocomplete - only fetch when modal is open
  const {
    options: customerTypeOptions,
    loading: customerTypeLoading,
    filterOptions: customerTypeFilterOptions
  } = useListCustomerTypesAutocomplete(customerTypeSearchTerm, 1000, undefined, !open);

  const handleCustomerTypeInputChange = (_: unknown, value: string) => {
    setCustomerTypeSearchTerm(value);
  };

  // Account Type autocomplete - only fetch when modal is open
  const {
    options: accountTypeOptions,
    loading: accountTypeLoading,
    filterOptions: accountTypeFilterOptions
  } = useListAccountTypesAutocomplete(accountTypeSearchTerm, 1000, undefined, !open);

  const handleAccountTypeInputChange = (_: unknown, value: string) => {
    setAccountTypeSearchTerm(value);
  };

  // Determine button text for Payment & Credit Terms section
  const paymentTermsButtonText = isPaymentTermsExpanded
    ? 'Hide Payment & Credit Terms'
    : isNew
      ? 'Add Payment & Credit Terms'
      : 'Show Payment & Credit Terms';

  return (
    <Grid2 container spacing={2} sx={{ paddingTop: '12px' }}>
      {/* Customer Information Section */}
      <Grid2 size={12}>
        <FormSectionTitle title="Customer Information" sx={sectionTitleSx} />
      </Grid2>
      <Grid2 size={12}>
        <FormikTextField<CustomerFormData>
          required
          id="companyName"
          label="Company Name"
          testId="customers:management:company-name-input"
        />
      </Grid2>
      <Grid2 size={6}>
        <FormikAutocomplete<Option, CustomerFormData>
          id="customerTypeId"
          label="Customer Type"
          testId="customers:management:type-autocomplete"
          required
          options={customerTypeOptions}
          optionsloading={customerTypeLoading}
          filterOptions={customerTypeFilterOptions}
          onInputChange={handleCustomerTypeInputChange}
          getOptionLabel={(option: Option) => option.label}
          isOptionEqualToValue={(option: Option, value: Option) => option.value === value.value}
          disablePortal={true}
        />
      </Grid2>
      <Grid2 size={6}>
        <FormikAutocomplete<Option, CustomerFormData>
          id="accountTypeId"
          label="Account Type"
          testId="customers:management:account-type-autocomplete"
          required
          options={accountTypeOptions}
          optionsloading={accountTypeLoading}
          filterOptions={accountTypeFilterOptions}
          onInputChange={handleAccountTypeInputChange}
          getOptionLabel={(option: Option) => option.label}
          isOptionEqualToValue={(option: Option, value: Option) => option.value === value.value}
          disablePortal={true}
        />
      </Grid2>
      <Grid2 size={6}>
        <FormikTextField<CustomerFormData> id="primaryPhone" label="Primary Phone" testId="customers:management:phone-input" />
      </Grid2>
      <Grid2 size={6}>
        <FormikTextField<CustomerFormData> id="primaryEmail" label="Primary Email" testId="customers:management:email-input" />
      </Grid2>

      {/* Address Information Section */}
      <Grid2 size={12}>
        <FormSectionTitle title="Address Information" sx={sectionTitleSx} />
      </Grid2>
      <Grid2 size={12}>
        <LocationForm hideCoordinates={true} />
      </Grid2>

      {/* Payment & Credit Terms Section - Collapsible */}
      <Grid2 size={12}>
        <FormSectionTitle title="Payment & Credit Terms" sx={sectionTitleSx} />
      </Grid2>
      <Grid2 size={12}>
        <Button
          startIcon={isPaymentTermsExpanded ? <RemoveCircleOutline /> : <AddCircleOutline />}
          onClick={() => setIsPaymentTermsExpanded(!isPaymentTermsExpanded)}
          {...tid('customers:management:toggle-payment-terms-button')}
          sx={{
            textTransform: 'uppercase',
            color: 'primary.main',
            fontWeight: 500,
            fontSize: '14px',
            letterSpacing: '0.5px',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          {paymentTermsButtonText}
        </Button>
      </Grid2>
      <Grid2 size={12}>
        <Collapse in={isPaymentTermsExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Grid2 container spacing={2}>
              <Grid2 size={6}>
                <FormikAutocomplete<OptionType, CustomerFormData>
                  id="pricingSchemeId"
                  label="Pricing Scheme"
                  testId="customers:management:pricing-scheme-autocomplete"
                  options={pricingSchemeOptions}
                  optionsloading={false}
                  filterOptions={filterOptions}
                  getOptionLabel={(option: OptionType) => option.label}
                  isOptionEqualToValue={(option: OptionType, value: OptionType) => option.value === value.value}
                  disablePortal={true}
                />
              </Grid2>
              <Grid2 size={6}>
                <FormikAutocomplete<OptionType, CustomerFormData>
                  id="termId"
                  label="Terms"
                  testId="customers:management:term-autocomplete"
                  options={termOptions}
                  optionsloading={false}
                  filterOptions={filterOptions}
                  getOptionLabel={(option: OptionType) => option.label}
                  isOptionEqualToValue={(option: OptionType, value: OptionType) => option.value === value.value}
                  disablePortal={true}
                />
              </Grid2>
              <Grid2 size={6}>
                <FormikAutocomplete<OptionType, CustomerFormData>
                  id="salesRegionId"
                  label="Sales Region"
                  testId="customers:management:sales-region-autocomplete"
                  options={salesRegionOptions}
                  optionsloading={false}
                  filterOptions={filterOptions}
                  getOptionLabel={(option: OptionType) => option.label}
                  isOptionEqualToValue={(option: OptionType, value: OptionType) => option.value === value.value}
                  disablePortal={true}
                />
              </Grid2>
              <Grid2 size={6}>
                <FormikAutocomplete<OptionType, CustomerFormData>
                  id="salesAgentId"
                  label="Sales Agent"
                  required
                  testId="customers:management:sales-agent-autocomplete"
                  options={salesAgentOptions}
                  optionsloading={formOptionsLoading}
                  filterOptions={filterOptions}
                  getOptionLabel={(option: OptionType) => option.label}
                  isOptionEqualToValue={(option: OptionType, value: OptionType) => option.value === value.value}
                  disablePortal={true}
                />
              </Grid2>
              <Grid2 size={12}>
                <FormikTextField<CustomerFormData> id="taxNumber" label="Tax Number" testId="customers:management:tax-number-input" />
              </Grid2>
            </Grid2>
          </Box>
        </Collapse>
      </Grid2>

      {/* Notes Section */}
      <Grid2 size={12}>
        <FormSectionTitle title="Notes" sx={sectionTitleSx} />
      </Grid2>
      <Grid2 size={12}>
        <FormikTextField<CustomerFormData>
          id="notes"
          label="Notes"
          multiline
          rows={4}
          placeholder="Add notes about this customer"
          testId="customers:management:notes-input"
        />
      </Grid2>
    </Grid2>
  );
}
